import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isHexColor } from '@/shared/lib/color';

import { useColorLibrary } from './ColorLibrary';
import styles from './ColorPicker.module.css';

export interface ColorOption {
  value: string;
  /** The accessible name (and tooltip) of the swatch: colors are never announced by look alone. */
  label: string;
}

interface Props {
  label: string;
  /** The current color; `null` = none / default; any non-`#rrggbb` text (`'mixed'`) = nothing is on. */
  value: string | null;
  options: readonly ColorOption[];
  onPick: (color: string | null) => void;
  /** Offer a "no color / default" choice with this accessible name. */
  noneLabel?: string;
  /** Allow any `#rrggbb`: the browser's own color editor plus a HEX field. */
  custom?: boolean;
  disabled?: boolean;
  /** The color the browser's editor starts from while `value` is none. */
  fallback?: string;
  /** The workspace this color is for (its shared palettes come first in the library). */
  workspaceId?: string;
}

const normalize = (draft: string): string | null => {
  const text = draft.trim().toLowerCase();
  const hex = text.startsWith('#') ? text : `#${text}`;
  return isHexColor(hex) ? hex : null;
};

/**
 * THE color control of the app: a preset palette, an optional "none", and - where the data model
 * takes any `#rrggbb` - a "Custom" swatch that opens the browser's own color editor (spectrum, hue
 * slider, HEX / RGB, eyedropper) and a HEX field. Every choice is ONE change: the editor reports
 * when it is closed (not while it is dragged) and the HEX field applies on Enter or when it loses
 * focus; Escape in the field puts the stored value back. Only `#rrggbb` ever leaves it (never free
 * CSS). Real buttons and inputs: keyboard operable, no hover-only affordances, larger on touch.
 */
export function ColorPicker({
  label,
  value,
  options,
  onPick,
  noneLabel,
  custom = true,
  disabled = false,
  fallback = '#0079bf',
  workspaceId,
}: Props) {
  const { t } = useTranslation();
  const renderLibrary = useColorLibrary();
  const id = useId();
  const current = value !== null && isHexColor(value) ? value : null;
  const isPreset = current !== null && options.some((option) => option.value === current);
  const customOn = custom && current !== null && !isPreset;
  const shown = current !== null && isHexColor(current) ? current.toLowerCase() : null;

  // The browser's editor fires `input` while it is dragged and `change` when it is closed:
  // only the latter is a choice (React's onChange listens to the former).
  const pickerRef = useRef<HTMLInputElement>(null);
  const [live, setLive] = useState<string | null>(null);
  useEffect(() => {
    const input = pickerRef.current;
    if (!input) return;
    const onChange = () => {
      onPick(input.value.toLowerCase());
    };
    input.addEventListener('change', onChange);
    return () => {
      input.removeEventListener('change', onChange);
    };
  }, [custom, onPick]);

  // The editor's value is shown while it is open; the stored value takes over once it changes.
  const [clearedFor, setClearedFor] = useState(current);
  if (clearedFor !== current) {
    setClearedFor(current);
    setLive(null);
  }

  const swatchColor = live ?? (customOn ? shown : null);
  const stored = customOn ? (shown ?? '') : '';
  const [draft, setDraft] = useState(stored);
  const [seen, setSeen] = useState(stored);
  const [invalid, setInvalid] = useState(false);
  if (seen !== stored) {
    setSeen(stored);
    setDraft(stored);
    setInvalid(false);
  }
  const submit = () => {
    if (draft.trim() === stored) {
      setInvalid(false);
      return;
    }
    if (draft.trim() === '') {
      setDraft(stored);
      setInvalid(false);
      return;
    }
    const color = normalize(draft);
    if (color) {
      setInvalid(false);
      onPick(color);
    } else {
      setInvalid(true);
    }
  };

  return (
    <div className={styles.picker} role="group" aria-label={label}>
      <div className={styles.swatches} role="radiogroup" aria-label={label}>
        {noneLabel !== undefined && (
          <button
            type="button"
            role="radio"
            aria-checked={value === null}
            aria-label={noneLabel}
            title={noneLabel}
            disabled={disabled}
            className={`${styles.swatch} ${styles.none}`}
            onClick={() => {
              onPick(null);
            }}
          />
        )}
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={current?.toLowerCase() === option.value.toLowerCase()}
            aria-label={option.label}
            title={option.label}
            disabled={disabled}
            className={styles.swatch}
            style={{ background: option.value }}
            onClick={() => {
              onPick(option.value);
            }}
          />
        ))}
        {custom && (
          <label
            className={`${styles.swatch} ${styles.customSwatch} ${customOn ? styles.customOn : ''}`}
            {...(swatchColor && { style: { background: swatchColor } })}
            title={t('common.customColor')}
            data-active={customOn || undefined}
          >
            <span className="visually-hidden">{`${label}: ${t('common.customColor')}`}</span>
            <input
              ref={pickerRef}
              type="color"
              disabled={disabled}
              value={live ?? shown ?? fallback}
              onChange={(event) => {
                setLive(event.target.value.toLowerCase());
              }}
              onBlur={() => {
                setLive(null);
              }}
            />
          </label>
        )}
      </div>
      {custom && (
        <div className={styles.hexRow}>
          <input
            type="text"
            className={styles.hex}
            inputMode="text"
            maxLength={7}
            spellCheck={false}
            autoComplete="off"
            placeholder="#rrggbb"
            disabled={disabled}
            aria-label={`${label}: ${t('common.colorHexLabel')}`}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? `${id}-error` : undefined}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setInvalid(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              } else if (event.key === 'Escape' && draft !== stored) {
                event.preventDefault();
                event.stopPropagation(); // reverts the draft; the dialog behind stays open
                setDraft(stored);
                setInvalid(false);
              }
            }}
            onBlur={submit}
          />
          {invalid && (
            <p id={`${id}-error`} className={styles.invalid} role="alert">
              {t('common.colorInvalid')}
            </p>
          )}
        </div>
      )}
      {custom &&
        renderLibrary?.({
          label,
          value: current,
          workspaceId,
          disabled,
          onPick: (color) => {
            onPick(color);
          },
        })}
    </div>
  );
}
